import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post } from '../api/client.js';
import { useAuth } from '../api/auth.js';
import { Badge, Card, Empty, Failure, Loading, Notice } from '../components/ui.js';
import { DEPLOY_STAGE, formatBytes, formatDateTime, formatRelative } from '../lib/format.js';

interface DeploymentSummary {
  id: string;
  revision: number;
  stage: string;
  note: string | null;
  device_count: number;
  horizon_from: string;
  horizon_to: string;
  created_at: string;
  published_at: string | null;
  applied_count: number;
  pending_count: number;
  failed_count: number;
}

interface DeploymentDetail {
  id: string;
  revision: number;
  stage: string;
  note: string | null;
  horizon_from: string;
  horizon_to: string;
  published_at: string | null;
  devices: Array<{
    device_id: string;
    device_name: string;
    school_name: string;
    stage: string;
    event_count: number;
    asset_count: number;
    bytes_total: number;
    manifest_hash: string;
    attempts: number;
    error: string | null;
    error_code: string | null;
    applied_at: string | null;
    last_seen_at: string | null;
    applied_revision: number;
    reason: string | null;
  }>;
}

export function DeploymentsPage(): JSX.Element {
  const { can } = useAuth();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['deployments'],
    queryFn: () => get<{ items: DeploymentSummary[]; total: number }>('/deployments?page_size=25'),
    refetchInterval: 15_000,
  });

  const detail = useQuery({
    queryKey: ['deployment', selected],
    queryFn: () => get<DeploymentDetail>(`/deployments/${selected}`),
    enabled: Boolean(selected),
    refetchInterval: 10_000,
  });

  const retry = useMutation({
    mutationFn: (vars: { deploymentId: string; deviceId: string }) =>
      post(`/deployments/${vars.deploymentId}/devices/${vars.deviceId}/retry`),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['deployment', selected] });
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      {error ? <Notice tone="danger">{error}</Notice> : null}

      <Card title="إصدارات النشر" subtitle="كل إصدار غير قابل للتعديل">
        {list.isLoading ? (
          <Loading />
        ) : list.error ? (
          <Failure error={list.error} onRetry={() => void list.refetch()} />
        ) : list.data!.items.length === 0 ? (
          <Empty title="لم يُنشر أي جدول بعد" hint="أنشئ قالبًا ثم اعتمد معاينة النشر." />
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>النسخة</th>
                  <th>الحالة</th>
                  <th>الأجهزة</th>
                  <th>الأفق</th>
                  <th>وقت النشر</th>
                  <th>الملاحظة</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.data!.items.map((d) => (
                  <tr key={d.id}>
                    <td className="num">{d.revision}</td>
                    <td>
                      <Badge tone={d.stage === 'PUBLISHED' ? 'ok' : d.stage === 'FAILED' ? 'danger' : 'muted'}>
                        {d.stage}
                      </Badge>
                    </td>
                    <td style={{ fontSize: 13 }}>
                      <Badge tone="ok" icon="✔">
                        {d.applied_count}
                      </Badge>{' '}
                      <Badge tone="muted" icon="…">
                        {d.pending_count}
                      </Badge>{' '}
                      {d.failed_count > 0 ? (
                        <Badge tone="danger" icon="✖">
                          {d.failed_count}
                        </Badge>
                      ) : null}
                      <div style={{ color: 'var(--muted)', fontSize: 12 }}>
                        من <span className="num">{d.device_count}</span>
                      </div>
                    </td>
                    <td className="mono" style={{ fontSize: 12 }}>
                      {d.horizon_from} → {d.horizon_to}
                    </td>
                    <td style={{ fontSize: 12.5 }}>{formatDateTime(d.published_at)}</td>
                    <td style={{ fontSize: 12.5, color: 'var(--muted)' }}>{d.note ?? '—'}</td>
                    <td className="actions">
                      <button onClick={() => setSelected(d.id)}>تفاصيل</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {selected ? (
        <Card
          title={`تفاصيل النسخة ${detail.data?.revision ?? ''}`}
          subtitle="حالة كل جهاز وسبب التأخر"
          actions={<button onClick={() => setSelected(null)}>إغلاق</button>}
        >
          {detail.isLoading ? (
            <Loading />
          ) : detail.error ? (
            <Failure error={detail.error} />
          ) : (
            <>
              <Notice tone="info">
                «طُبّق» تعني أن الجهاز أكّد تطبيق البيان الموقّع بنفس البصمة. الإرسال وحده لا يكفي.
              </Notice>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>الجهاز</th>
                      <th>المدرسة</th>
                      <th>الحالة</th>
                      <th>الأحداث</th>
                      <th>الحجم</th>
                      <th>المحاولات</th>
                      <th>السبب</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data!.devices.map((d) => {
                      const stage = DEPLOY_STAGE[d.stage] ?? DEPLOY_STAGE.PENDING!;
                      return (
                        <tr key={d.device_id}>
                          <td>{d.device_name}</td>
                          <td>{d.school_name}</td>
                          <td>
                            <Badge tone={stage.tone} icon={stage.icon}>
                              {stage.label}
                            </Badge>
                            {d.applied_at ? (
                              <div style={{ fontSize: 12, color: 'var(--muted)' }}>
                                {formatDateTime(d.applied_at)}
                              </div>
                            ) : null}
                          </td>
                          <td className="num">{d.event_count}</td>
                          <td className="num">{formatBytes(d.bytes_total)}</td>
                          <td className="num">{d.attempts}</td>
                          <td style={{ fontSize: 12.5 }}>
                            {d.reason ?? '—'}
                            {d.error_code ? (
                              <div className="mono" style={{ color: 'var(--danger)' }}>
                                {d.error_code}
                              </div>
                            ) : null}
                            <div style={{ color: 'var(--muted)' }}>
                              آخر اتصال: {formatRelative(d.last_seen_at)}
                            </div>
                          </td>
                          <td className="actions">
                            {can('schedule.publish') && d.stage !== 'APPLIED' && d.stage !== 'SUPERSEDED' ? (
                              <button
                                className="ghost"
                                disabled={retry.isPending}
                                onClick={() =>
                                  retry.mutate({ deploymentId: selected, deviceId: d.device_id })
                                }
                              >
                                إعادة المحاولة
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </Card>
      ) : null}
    </>
  );
}
